/**
 * @file products.store.ts
 *
 * Zustand store for the products list and the flat category list used by the
 * filter dropdown. Mirrors the Website Builder v2 store shape: `fetchProducts`
 * replaces the current page and records pagination totals; mutations report
 * success via their return value and surface failures through `error`. The store
 * holds products as the WIRE truth (rich-text fields unstripped, so a form save
 * round-trips them unchanged); screens wanting plain text strip at the display
 * site via {@link stripHtmlForDisplay}. The create/edit/duplicate form lifecycle
 * (`current`/`loadOne`/`submitCreate`/`submitUpdate`/`resetForm`) comes from the
 * shared {@link createFormSlice}.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  fetchProducts as apiFetchProducts,
  fetchProductById as apiFetchProductById,
  createProduct as apiCreateProduct,
  updateProduct as apiUpdateProduct,
  deleteProduct as apiDeleteProduct,
} from '../api/products';
import { fetchProductCategories as apiFetchProductCategories } from '../api/categories';
import type {
  FetchProductsParams,
  ShopCategory,
  ShopProduct,
  ShopProductPayload,
  ShopProductVariant,
} from '../api/shop-types';
import { createFormSlice, toErrorCode, toMessage, type FormSlice } from './store-utils';

/** The product form's save intent: the aggregate payload plus the edited per-variant rows. */
export interface SaveProductArgs {
  mode: 'create' | 'edit' | 'duplicate';
  /** The product id (edit only). */
  id?: string;
  payload: ShopProductPayload;
  /** The form's current per-variant table rows (empty for a simple/digital product). */
  variantRows: ShopProductVariant[];
}

/**
 * Removes HTML tags from a rich-text field, leaving trimmed plain text. DISPLAY-ONLY:
 * call it where a screen renders a description as text (the drawer's preview) — never on
 * the stored product. Stripping on read used to feed the form the stripped text, which it
 * wrote back verbatim, silently flattening every rich-text description on any save.
 *
 * @param value - The raw (possibly rich-text) field.
 *
 * @returns The trimmed plain text.
 */
export function stripHtmlForDisplay(value: string): string {
  if (!value) {
    return '';
  }
  return value.replace(/<[^>]*>/g, '').trim();
}

/**
 * Normalizes a raw product for the store. Deliberately does NOT strip markup: the form
 * seeds from these fields and writes them back, so the store must hold the wire truth —
 * a save round-trips rich text unchanged. Screens that want plain text strip at the
 * display site via {@link stripHtmlForDisplay}.
 *
 * @param raw - The product as returned by the API.
 *
 * @returns The product, unmodified (the seam is kept for future read-side normalization).
 */
export function buildShopProductFromApi(raw: ShopProduct): ShopProduct {
  return raw;
}

/**
 * Shape of the products store: the list/filter state plus the shared
 * {@link FormSlice} (`current`/`loadOne`/`submitCreate`/`submitUpdate`/…) that
 * seeds and persists the create/edit/duplicate form.
 */
interface ProductsState extends FormSlice<ShopProduct, ShopProductPayload, string> {
  products: ShopProduct[];
  productsTotal: number;
  productsTotalPages: number;
  categories: ShopCategory[];
  loading: boolean;
  error: string | null;

  /**
   * Fetches a page of products, replacing the current list.
   *
   * @param params - Pagination, search, category, and sort options.
   */
  fetchProducts: (params?: FetchProductsParams) => Promise<void>;

  /** Fetches the flat category list for the filter dropdown. */
  fetchCategories: () => Promise<void>;

  /**
   * Permanently deletes a product.
   *
   * @param id - The product id to delete.
   *
   * @returns `true` on success, `false` when the request fails.
   */
  deleteProduct: (id: string) => Promise<boolean>;

  /**
   * Persists the product form through the batch-vs-aggregate dispatch — the seam that threads
   * the per-variant edits. The pre-edit variant baseline for the diff is read from `current`
   * (the loaded full product). Sets `formError`/`formErrorCode` on failure, like the generic
   * create/update. The generic {@link FormSlice.submitCreate}/{@link FormSlice.submitUpdate}
   * remain for the no-variant path (and the store tests); the form uses this instead.
   *
   * @param args - The mode, id, aggregate payload, and edited per-variant rows.
   *
   * @returns The saved product (normalized), or `null` when the request fails.
   */
  saveProductForm: (args: SaveProductArgs) => Promise<ShopProduct | null>;
}

export const useProductsStore = create<ProductsState>()(
  devtools(
    (set, get) => ({
      products: [],
      productsTotal: 0,
      productsTotalPages: 0,
      categories: [],
      loading: false,
      error: null,

      ...createFormSlice<ProductsState, ShopProduct, ShopProductPayload, ShopProduct, string>({
        name: 'products',
        fetchById: apiFetchProductById,
        create: apiCreateProduct,
        update: (id, payload) =>
          apiUpdateProduct(id, payload, get().current?.attributes ?? []),
        // The list holds lighter `/v1` summaries (no description, categories,
        // attributes, weight, or downloads), so the form and detail drawer must
        // always fetch the full product by id rather than seed from the cache.
        selectCached: () => () => undefined,
        normalize: buildShopProductFromApi,
      })(set, get),

      fetchProducts: async (params = {}) => {
        set({ loading: true, error: null }, false, 'products/fetch/pending');
        try {
          const res = await apiFetchProducts(params);
          set(
            {
              products: res.data.map(buildShopProductFromApi),
              productsTotal: res.total,
              productsTotalPages: res.totalPages,
              loading: false,
            },
            false,
            'products/fetch/fulfilled',
          );
        } catch (err) {
          set(
            { loading: false, error: toMessage(err) },
            false,
            'products/fetch/rejected',
          );
        }
      },

      fetchCategories: async () => {
        try {
          const categories = await apiFetchProductCategories();
          set({ categories }, false, 'products/fetchCategories/fulfilled');
        } catch (err) {
          set({ error: toMessage(err) }, false, 'products/fetchCategories/rejected');
        }
      },

      deleteProduct: async (id) => {
        try {
          await apiDeleteProduct(id);
          return true;
        } catch (err) {
          set({ error: toMessage(err) }, false, 'products/delete/rejected');
          return false;
        }
      },

      saveProductForm: async ({ mode, id, payload, variantRows }) => {
        set({ formError: null, formErrorCode: null }, false, 'products/save/pending');
        try {
          if (mode === 'edit' && id !== undefined) {
            const previous = get().current;
            const saved = await apiUpdateProduct(
              id,
              payload,
              previous?.attributes ?? [],
              variantRows,
              previous?.variants ?? [],
              previous?.updated_at,
            );
            return buildShopProductFromApi(saved);
          }
          const created = await apiCreateProduct(payload, variantRows);
          return buildShopProductFromApi(created);
        } catch (err) {
          set(
            { formError: toMessage(err), formErrorCode: toErrorCode(err) },
            false,
            'products/save/rejected',
          );
          return null;
        }
      },
    }),
    { name: 'shop/products' },
  ),
);
